/**
 * One place's history, newest first (#420): who placed it, who moved it, what the
 * administrator answered — and, on any row that left the place somewhere, *Put this back*.
 *
 * A locked place is trusted by every search, so a mistake in one has to be visible and
 * undoable; this is where both happen.
 *
 * The joins made to it from Reports → Адреси за заключване (#434) are in the same list, by
 * when they were made: what row was joined, what it took, and *Undo* — or, for one cut off
 * before it finished, *Finish* as well. A join changes nothing about the place itself, so it
 * has no version to put back.
 */

import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../../shell/formats';
import type { PlaceHistoryEntry, PlaceJoinEntry } from '../../places/api';
import { countOptions } from '../../reports/addressesQuery';
import { Button, Pill, Section } from '../../ui';
import { who, whatChanged } from './words';

type Line = { kind: 'change'; at: string; entry: PlaceHistoryEntry; index: number } | { kind: 'join'; at: string; join: PlaceJoinEntry };

export function PlaceHistoryList({
  entries,
  joins = [],
  people,
  busy,
  onRestore,
  onUndoJoin,
  onFinishJoin,
}: {
  entries: PlaceHistoryEntry[];
  joins?: PlaceJoinEntry[];
  people: Readonly<Record<string, string>>;
  busy: boolean;
  onRestore: (entry: PlaceHistoryEntry) => void;
  onUndoJoin?: (join: PlaceJoinEntry) => void;
  onFinishJoin?: (join: PlaceJoinEntry) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const lines: Line[] = [
    ...entries.map((entry, index): Line => ({ kind: 'change', at: entry.at, entry, index })),
    ...joins.map((join): Line => ({ kind: 'join', at: join.at, join })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <Section title={t('places.history.title')}>
      <ul className="m-0 p-0 list-none flex flex-col">
        {lines.map((line) =>
          line.kind === 'join' ? (
            <JoinLine
              key={line.join.joinId}
              join={line.join}
              people={people}
              busy={busy}
              onUndo={onUndoJoin}
              onFinish={onFinishJoin}
            />
          ) : (
            <li
              key={line.entry.id}
              className="flex flex-wrap items-center justify-between gap-2 px-5 py-2 border-b border-rule last:border-b-0"
            >
              <span className="flex flex-col gap-0.5 min-w-0">
                <span className="flex items-center gap-2">
                  {t(`places.history.${line.entry.kind}`)}
                  {line.entry.state !== 'final' && (
                    <Pill
                      tone={line.entry.state === 'waiting' ? 'warn' : line.entry.state === 'rejected' ? 'danger' : 'ok'}
                      soft
                    >
                      {t(`places.history.state.${line.entry.state}`)}
                    </Pill>
                  )}
                </span>
                <span className="text-secondary text-ink-2">
                  {line.entry.kind === 'reported'
                    ? line.entry.after == null
                      ? t('places.review.reportNoSpot')
                      : t('places.history.reportedSpot')
                    : whatChanged(t, line.entry.before, line.entry.after)}
                </span>
                <span className="text-secondary text-ink-3">
                  {who(t, line.entry.by, line.entry.byUserId, people)} · {formats.moment(line.entry.at)}
                  {line.entry.answeredAt != null &&
                    ` · ${t('places.history.answered', { name: line.entry.answeredBy == null ? '' : (people[line.entry.answeredBy] ?? ''), at: formats.moment(line.entry.answeredAt) })}`}
                </span>
              </span>
              {/* Every version but the one the place is now, which is the first change. */}
              {line.index > 0 && line.entry.after != null && line.entry.kind !== 'reported' && (
                <Button disabled={busy} onClick={() => onRestore(line.entry)}>
                  {t('places.history.restore')}
                </Button>
              )}
            </li>
          ),
        )}
      </ul>
    </Section>
  );
}

function JoinLine({
  join,
  people,
  busy,
  onUndo,
  onFinish,
}: {
  join: PlaceJoinEntry;
  people: Readonly<Record<string, string>>;
  busy: boolean;
  onUndo?: (join: PlaceJoinEntry) => void;
  onFinish?: (join: PlaceJoinEntry) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-5 py-2 border-b border-rule last:border-b-0">
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="flex items-center gap-2">
          {t('places.joins.row')}
          {join.state === 'pending' && (
            <Pill tone="warn" soft>
              {t('places.joins.pending')}
            </Pill>
          )}
          {join.state === 'undone' && (
            <Pill tone="danger" soft>
              {t('places.joins.undone')}
            </Pill>
          )}
        </span>
        <span className="text-secondary text-ink-2">
          {t('places.joins.entry', {
            typed: join.typed,
            orders: t('counts.bookings', countOptions(join.orders, formats.count)),
            destinations: t('counts.destinations', countOptions(join.destinations, formats.count)),
            addresses: t('counts.addresses', countOptions(join.addresses, formats.count)),
          })}
        </span>
        <span className="text-secondary text-ink-3">
          {join.byUserId == null ? '' : `${people[join.byUserId] ?? ''} · `}
          {formats.moment(join.at)}
          {join.undoneAt != null &&
            ` · ${t('places.joins.undoneBy', { name: join.undoneBy == null ? '' : (people[join.undoneBy] ?? ''), at: formats.moment(join.undoneAt) })}`}
        </span>
      </span>
      {join.state !== 'undone' && (
        <span className="flex items-center gap-2">
          {join.state === 'pending' && onFinish !== undefined && (
            <Button disabled={busy} onClick={() => onFinish(join)}>
              {t('places.joins.finish')}
            </Button>
          )}
          {onUndo !== undefined && (
            <Button disabled={busy} onClick={() => onUndo(join)}>
              {t('places.joins.undo')}
            </Button>
          )}
        </span>
      )}
    </li>
  );
}
